/** Costs: what the service is spending, worked out from its own counters.
 *
 * The landing page promises "about $6–15 a month", and a promise nobody can
 * check is a guess. So this screen shows the arithmetic rather than a single
 * number: Cloudflare's published prices applied to usage the service has
 * recorded in its own tables, with the free allowance subtracted line by line.
 *
 * It is not a bill, and the screen says so in three places rather than one —
 * in the line under the four cards, on every row whose usage was modelled
 * instead of counted, and in the list of what these figures are known to miss.
 * That candour is the point. `src/domain/costs.ts` names its own weak spots in
 * `warnings`, and hiding them here to make the page tidier would turn an
 * honest estimate into a quiet claim to be a bill.
 *
 * The one figure the service cannot supply is the budget alert: there is no
 * budget setting and no webhook to fire, so the fourth card says that plainly
 * instead of printing a number nothing would enforce.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AdminHeader } from '@/components/admin/AdminHeader';
import {
  Bar,
  Card,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  Sparkline,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { adminCosts } from '@/lib/api';
import { DASH, changePercent, count, money, percent, relativeTime } from '@/lib/format';
import type { CSSProperties } from 'react';
import type { AdminCosts as CostsReport, CostResource } from '@/lib/types';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** `2026-09` → `Sep`, or `September`. Parsed by hand: this is a month id, not
 *  a date, and `Date.parse` on `YYYY-MM` is not portable. */
function monthName(id: string, long = false): string {
  const parts = id.split('-');
  const index = Number(parts[1]) - 1;
  const table = long ? LONG_MONTHS : MONTHS;
  return table[index] ?? id;
}

/** The month before `YYYY-MM`, for "vs August". */
function previousMonthName(id: string): string {
  const parts = id.split('-');
  const month = Number(parts[1]);
  if (!Number.isFinite(month)) return 'last month';
  const index = month - 2;
  return LONG_MONTHS[index < 0 ? 11 : index] ?? 'last month';
}

/** `Sept 1–16` — the window the month-to-date figure covers. */
function monthWindow(report: CostsReport): string {
  return `${monthName(report.month.id)} 1–${Math.max(1, Math.round(report.month.elapsed_days))}`;
}

const PANEL: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-sm)',
};

const FIGURE: CSSProperties = {
  fontSize: 30,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
};

/**
 * The one rule this screen cannot write inline.
 *
 * The design's lower half is a 1.5 : 1 split — the resource table earns the
 * extra room and the bars beside it do not — and a ratio is not something
 * `auto-fit` can express. So the two-column form is stated here with a query
 * that drops it to one column before the table's column is squeezed into
 * uselessness. It is scoped to a class this screen owns and touches nothing
 * else; every other rule on the page is a token or a utility.
 */
const SPLIT_CSS = `
.admin-costs-split {
  display: grid;
  gap: 16px;
  align-items: start;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
}
@media (max-width: 900px) {
  .admin-costs-split { grid-template-columns: minmax(0, 1fr); }
}
`;

/** Green when the projection is below last month, amber when it is above.
 *  Cheaper than last month is the good news here, so the usual polarity of a
 *  rising number is inverted deliberately. */
function changeColour(change: number | null): string {
  if (change === null) return 'var(--color-neutral-500)';
  if (change < 0) return 'var(--color-ok)';
  if (change > 0) return 'var(--color-warn)';
  return 'var(--color-neutral-400)';
}

/** A free tier nearly spent is the thing worth noticing on this table. */
function barTone(pct: number): 'accent' | 'warn' {
  return pct >= 90 ? 'warn' : 'accent';
}

export default function AdminCosts() {
  const [report, setReport] = useState<CostsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reply = await adminCosts();
      if (!alive.current) return;
      setReport(reply);
      setError(null);
    } catch (thrown) {
      if (!alive.current) return;
      setError(thrown);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && report === null) {
    return (
      <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AdminHeader />
        <ErrorNote error={error} title="the costs could not be worked out" onRetry={() => void load()} />
      </Page>
    );
  }

  if (!report) {
    return (
      <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AdminHeader />
        <LoadingPanel label="counting" />
      </Page>
    );
  }

  const { totals, month, currency } = report;
  const trendValues = report.trend.map((entry) => entry.cost_usd);
  const trendLabels = report.trend.map(
    (entry) => `${monthName(entry.month)}${entry.partial ? '*' : ''}`,
  );

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AdminHeader />

      <section aria-label="Costs" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {loading ? (
          <span className="sr-only" role="status">
            refreshing the estimate
          </span>
        ) : null}

        {/* ── the four figures ───────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          <Card>
            <Kicker>this month so far</Kicker>
            <span style={FIGURE}>{money(totals.this_month_usd, currency)}</span>
            <span className="card-meta">
              day {count(Math.round(month.elapsed_days))} of {count(month.days)} · estimated
            </span>
          </Card>

          <Card>
            <Kicker>projected</Kicker>
            <span style={FIGURE}>{money(totals.projected_usd, currency)}</span>
            <span className="card-meta">
              {totals.last_month_usd === null ? (
                <span>no previous month to compare with</span>
              ) : (
                <>
                  <span style={{ color: changeColour(totals.change_pct) }}>
                    {changePercent(totals.change_pct)}
                  </span>
                  &nbsp;vs {previousMonthName(month.id)} (
                  {money(totals.last_month_usd, currency)})
                </>
              )}
            </span>
          </Card>

          <Card>
            <Kicker>per active user</Kicker>
            <span style={FIGURE}>{money(totals.per_active_user_usd, currency)}</span>
            <span className="card-meta">
              {count(totals.active_users)} account{totals.active_users === 1 ? '' : 's'} uploaded
              this month
            </span>
          </Card>

          <Card>
            <Kicker>budget alert</Kicker>
            <span style={{ ...FIGURE, color: 'var(--color-neutral-500)' }}>{DASH}</span>
            <span className="card-meta" style={{ textWrap: 'pretty' }}>
              no budget is set on this server — a webhook would fire at 80 %
            </span>
          </Card>
        </div>

        <p
          style={{
            margin: 0,
            fontSize: 11.5,
            color: 'var(--color-neutral-600)',
            textWrap: 'pretty',
          }}
        >
          An estimate, not a bill. {report.basis} Nothing here is read from a billing API, and it
          will not match an invoice. Worked out {relativeTime(report.generated_at)}.
        </p>

        {/* ── resources and data types ───────────────────────────────── */}
        <style>{SPLIT_CSS}</style>
        <div className="admin-costs-split">
          <div style={PANEL}>
            <div
              style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <Kicker>by resource</Kicker>
              <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                usage → cost after free tier · {monthWindow(report)}
              </span>
            </div>

            <Table scroll label="Cost by resource" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <THead>
                <TR>
                  <TH>Resource</TH>
                  <TH>Driven by</TH>
                  <TH numeric>Usage</TH>
                  <TH numeric>Free tier</TH>
                  <TH numeric>Cost</TH>
                  <TH style={{ minWidth: 100 }}>Free tier used</TH>
                </TR>
              </THead>
              <TBody>
                {report.lines.length === 0 ? (
                  <TR>
                    <TD colSpan={6} muted>
                      Nothing has been used yet, so there is nothing to price.
                    </TD>
                  </TR>
                ) : (
                  report.lines.map((line) => (
                    <ResourceRow key={line.id} line={line} currency={currency} />
                  ))
                )}
              </TBody>
            </Table>

            <p
              style={{
                margin: 0,
                padding: '10px 16px',
                fontSize: 11.5,
                color: 'var(--color-neutral-600)',
                textWrap: 'pretty',
              }}
            >
              {report.storage_note}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <Kicker>by data type</Kicker>
                <span className="card-meta">this month</span>
              </div>
              {report.by_type.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
                  nothing has been attributed to a data type yet
                </span>
              ) : null}
              {report.by_type.map((entry) => (
                <div
                  key={entry.type}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '5px 0' }}
                >
                  <div
                    className="tabular-nums"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={`${entry.type} — ${entry.note}`}
                    >
                      <code style={{ fontFamily: 'var(--font-mono)' }}>{entry.type}</code>{' '}
                      <span style={{ color: 'var(--color-neutral-500)', fontSize: 11.5 }}>
                        {entry.note}
                      </span>
                    </span>
                    <span style={{ flex: 'none' }}>{money(entry.cost_usd, currency)}</span>
                  </div>
                  <Bar
                    value={entry.pct}
                    height={4}
                    label={`${entry.type}: ${percent(entry.pct)} of this month`}
                  />
                </div>
              ))}
            </Card>

            <Card>
              <Kicker>monthly trend</Kicker>
              {trendValues.length < 2 ? (
                <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
                  not enough history yet — a line needs a second month to be a line
                </span>
              ) : (
                <>
                  <Sparkline
                    values={trendValues}
                    labels={trendLabels}
                    markLast
                    height={70}
                    label={`cost per month, ${monthName(report.trend[0]?.month ?? month.id, true)} to ${monthName(month.id, true)}`}
                  />
                  <span className="card-meta" style={{ textWrap: 'pretty' }}>
                    every month but this one is reconstructed from the counters that survive
                    {trendLabels.some((label) => label.endsWith('*'))
                      ? ' · * rests on counters the cron has since swept'
                      : ''}
                  </span>
                </>
              )}
            </Card>

            {report.warnings.length > 0 ? (
              <Card>
                <Kicker>what this does not count</Kicker>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    fontSize: 11.5,
                    color: 'var(--color-neutral-500)',
                    textWrap: 'pretty',
                  }}
                >
                  {report.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        </div>
      </section>
    </Page>
  );
}

/** One row of the resource table.
 *
 * A modelled line wears a small "est." beside its name, with the reason in the
 * title — the note explains the assumption, and an assumption printed in full
 * would crowd out the figures. A line with no free allowance at all (the flat
 * plan charge) gets a dash rather than a full bar: the service reports it as
 * 100 % used because there is nothing left, but a bar at the top of its range
 * reads as a warning, and there is nothing here to be warned about.
 */
function ResourceRow({ line, currency }: { line: CostResource; currency: string }) {
  const hasFreeTier = line.free_allowance > 0;
  return (
    <TR>
      <TD nowrap title={line.note || undefined}>
        {line.resource}
        {line.estimated ? (
          <span
            style={{ marginLeft: 6, fontSize: 10, color: 'var(--color-warn)' }}
            title={line.note}
          >
            &nbsp;est.
          </span>
        ) : null}
      </TD>
      <TD muted nowrap>
        {line.driver}
      </TD>
      <TD numeric>{line.usage_label}</TD>
      <TD numeric muted>
        {hasFreeTier ? line.free_label : DASH}
      </TD>
      <TD numeric>{money(line.cost_usd, currency)}</TD>
      <TD style={{ minWidth: 100 }}>
        {hasFreeTier ? (
          <Bar
            value={line.free_used_pct}
            height={4}
            tone={barTone(line.free_used_pct)}
            label={`${line.resource}: ${percent(line.free_used_pct)} of the free tier used`}
          />
        ) : (
          <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
            no free tier
          </span>
        )}
      </TD>
    </TR>
  );
}
